import { Fragment, useEffect, useRef, useState } from 'react'
import {
    Controller,
    FormProvider,
    useFieldArray,
    useForm,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'
import { Plus, ShoppingCart, Truck, Users } from 'lucide-react'
import { toast } from 'sonner'

import { FormSection } from './form-section'
import { OrderItemRowRhf } from './order-item-row-rhf'
import { OrderMaterialSubRows } from './order-material-sub-rows'
import { CustomerSearchSelect } from './customer-search-select'
import { ProvinceWardPicker } from './province-ward-picker'
import { ShippingCarrierSelect } from './shipping-carrier-select'
import type { OrderWorkflowTracker } from '@/lib/order-workflow-tracking'
import type { OrderItemDraft } from './order-item-row'
import type {
    CreateOrderRequest,
    StockShortfallItem,
} from '@/lib/api/sales-order-api'
import type { Customer } from '@/lib/api/customer-api'
import type { PackagingDraftItem } from '@/lib/packaging-draft'
import type { LineSplitState } from '@/lib/material-split'
import type { PickedPackagingMaterial } from '@/components/packaging/packaging-material-picker'
import type { InvoiceBuyerFormValue } from '@/components/forms/invoice-buyer-fields'
import type { MoneyLine } from '@/lib/order-money'
import {
    trackOrderField,
    trackOrderValidation,
} from '@/lib/order-workflow-field'
import { ApiError } from '@/lib/api/client'
import { AwaitingPackagingDialog } from '@/components/orders/awaiting-packaging-dialog'
import { CodFreightFields } from '@/components/orders/cod-freight-fields'
import {
    DEFAULT_CREATE_INVOICE_BUYER,
    InvoiceBuyerFields,
    applyBuyerPatch,
    buildInvoiceBuyerPayload,
} from '@/components/forms/invoice-buyer-fields'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { buildManualRow } from '@/lib/packaging-draft'
import { PackagingMaterialPicker } from '@/components/packaging/packaging-material-picker'
import { OrderMaterialRow } from '@/components/forms/order-material-row'
import { newMaterialRowId, rowKeyOf } from '@/lib/material-row-id'
import { buildLineSortOrders, mergeOrderLines } from '@/lib/order-line-order'
import { useOrderPackagingSync } from '@/hooks/use-order-packaging-sync'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useCreateOrder } from '@/hooks/use-sales-order-query'
import { useWarehouses } from '@/hooks/use-inventory-query'
import { useInvoiceSettings } from '@/hooks/use-invoice-query'
import { formatVND, formatVnd } from '@/lib/format'
import { effectiveMaterialUnitPrice } from '@/lib/material-split'
import { orderHasSellableLine } from '@/lib/order-validity'
import { m } from '@/lib/paraglide/messages'
import { posthog } from '@/lib/posthog'
import { orderCreatedProps } from '@/lib/posthog-order-event'
import {
    DEFAULT_SHIPPING_CARRIER_ID,
    WALKIN_CUSTOMER,
    WALKIN_CUSTOMER_ID,
} from '@/lib/constants'
import { vatFromGross } from '@/lib/order-money'

function getSourceChannels() {
    return [
        { value: 'direct', label: m.order_channel_direct() },
        { value: 'phone', label: m.order_channel_phone() },
        { value: 'online', label: m.order_channel_online() },
        { value: 'agent', label: m.order_channel_agent() },
        { value: 'other', label: m.order_channel_other() },
    ]
}

const EMPTY_ITEM: OrderItemDraft = {
    product_id: '',
    product_name: '',
    unit: '',
    quantity: 1,
    unit_price: 0,
    discount_percent: 0,
    material_price_split: false,
    price_includes_material: false,
    material_price_per_unit: 0,
    master_unit_price: 0,
    row_seq: 0,
}

interface Props {
    workflow?: OrderWorkflowTracker
    defaultCustomerId?: string
    defaultCustomerName?: string
    defaultCustomerPhone?: string
    onSuccess?: () => void
    onCancel?: () => void
}

// makeOrderSchema builds the delivery-order validation schema. Exported so the
// rules can be unit-tested without rendering the form. Province and ward are
// required: the geographic report groups orders by their shipping province, so
// an order without a destination code would silently land in the "unknown"
// bucket and distort the regional revenue picture.
export function makeOrderSchema() {
    const orderItemSchema = z.object({
        product_id: z.string().uuid(m.validation_product_id_invalid()),
        product_name: z.string(),
        // Đơn vị được autofill khi chọn sản phẩm; giữ trong form để hiện cột
        // "Đơn vị" mà không cần lookup lại product list. Optional để không
        // vỡ đơn cũ chưa có unit.
        unit: z.string().optional(),
        quantity: z.coerce.number().positive(m.validation_quantity_positive()),
        unit_price: z.coerce
            .number()
            .min(0, m.order_validation_price_nonnegative()),
        discount_percent: z.coerce
            .number()
            .min(0, m.order_validation_discount_range())
            .max(100, m.order_validation_discount_range()),
        // Tách/gộp giá vật tư — chỉ áp dụng SP price_includes_material.
        material_price_split: z.boolean().optional(),
        price_includes_material: z.boolean().optional(),
        material_price_per_unit: z.coerce.number().optional(),
        master_unit_price: z.coerce.number().optional(),
        // Số thứ tự dòng trên bảng hàng hóa — chỉ để sắp xếp ở client, gửi lên
        // API dưới dạng sort_order đã đánh lại (xem @/lib/order-line-order).
        row_seq: z.coerce.number().optional(),
    })

    return z.object({
        source_channel: z.string().min(1),
        customer_phone: z
            .string()
            .min(1, m.validation_required_phone())
            .min(8, m.validation_phone_min())
            .max(20, m.validation_phone_max()),
        shipping_address: z
            .string()
            .min(1, m.validation_required_address())
            .min(5, m.validation_address_min())
            .max(500, m.validation_address_max()),
        shipping_province: z.string().optional(),
        shipping_province_code: z
            .string()
            .min(1, m.validation_required_province()),
        shipping_ward: z.string().optional(),
        shipping_ward_code: z.string().min(1, m.validation_required_ward()),
        product_notes: z.string().optional(),
        internal_order_code: z.string().max(64).optional(),
        shipping_carrier_id: z.string().min(1, m.validation_required()),
        // "Thu cước" checkbox maps to this field:
        // 'customer' = customer owes the freight (settled later at the
        // depot — the system no longer records an amount);
        // 'company' = company absorbs freight.
        // Form defaultValues always provides this; optional here so the
        // resolver type stays consistent with what RHF tracks as field values.
        shipping_fee_payer: z.enum(['customer', 'company']).optional(),
        // Whether to collect payment on delivery. Enforced at ConfirmDelivery.
        // Form defaultValues provides true; optional here for the same reason.
        collect_cod: z.boolean().optional(),
        // KHÔNG ép ≥1 sản phẩm: đơn có thể chỉ gồm vật tư có đơn giá. Ràng buộc
        // "phải có dòng bán được" (SP hoặc vật tư có giá) kiểm ở handleSubmit vì
        // vật tư nằm ở state riêng, ngoài schema.
        items: z.array(orderItemSchema),
    })
}

export function SalesOrderForm({
    defaultCustomerId,
    onSuccess,
    onCancel,
    workflow,
}: Props) {
    const schema = makeOrderSchema()

    type FormData = z.infer<typeof schema>

    // Popup "Kho không đủ hàng — tạo đơn chờ đóng gói?" hiện khi BE trả 409
    // INSUFFICIENT_STOCK và cashier chưa opt-in AwaitingPackaging.
    const [awaitShortfalls, setAwaitShortfalls] = useState<
        StockShortfallItem[] | null
    >(null)
    const createMutation = useCreateOrder({
        onErrorHandled: (err) => {
            if (
                err instanceof ApiError &&
                err.status === 409 &&
                err.code === 'INSUFFICIENT_STOCK'
            ) {
                const details = err.details as
                    { shortfalls?: StockShortfallItem[] } | undefined
                const shortfalls = details?.shortfalls
                if (shortfalls && shortfalls.length > 0) {
                    setAwaitShortfalls(shortfalls)
                    return true
                }
            }
            return false
        },
    })
    // Warehouse mặc định là nguồn tồn kho hiển thị trong ProductPicker.
    const { data: warehouses = [] } = useWarehouses()
    const defaultWarehouseId =
        warehouses.find((w) => w.is_default)?.id ?? warehouses[0]?.id
    const { data: invoiceSettings } = useInvoiceSettings()
    // VAT chỉnh được trên form (2 chiều: sửa % ↔ sửa tiền). Mặc định lấy từ cài
    // đặt hóa đơn, nhưng người bán ghi đè cho riêng đơn. Quản lý ngoài RHF (như
    // selectedCustomer/packagingItems). vatAmountOverride giữ số tiền người bán
    // gõ tay để dùng đúng số đó (tránh lệch làm tròn); null = suy từ %.
    const [vatPercent, setVatPercent] = useState(0)
    const [vatAmountOverride, setVatAmountOverride] = useState<number | null>(
        null,
    )
    const vatTouched = useRef(false)
    useEffect(() => {
        // Chỉ nạp mặc định từ cài đặt khi người bán CHƯA chạm vào VAT.
        if (!vatTouched.current && invoiceSettings?.vat_percent != null) {
            setVatPercent(invoiceSettings.vat_percent)
        }
    }, [invoiceSettings?.vat_percent])
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
        null,
    )
    // Tick "Trừ vào số dư khách" — chỉ hiện khi khách đang dư (current_debt < 0),
    // mặc định bật. Bật → không thu COD (đã trả từ số dư).
    const [applyCredit, setApplyCredit] = useState(false)
    const customerCredit = Math.max(0, -(selectedCustomer?.current_debt ?? 0))
    // Người mua công ty (HĐĐT) — ẩn sau flag dev-einvoice tới khi ký hợp đồng MISA.
    const einvoiceEnabled = useFeatureFlag('dev-einvoice')
    const [invoiceBuyer, setInvoiceBuyer] = useState<InvoiceBuyerFormValue>(
        DEFAULT_CREATE_INVOICE_BUYER,
    )
    // Phase 4 — vật tư bao bì sale chốt (autofill từ BOM, có thể edit).
    const [packagingItems, setPackagingItems] = useState<PackagingDraftItem[]>(
        [],
    )

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            source_channel: 'direct',
            customer_phone: '',
            shipping_address: '',
            shipping_province: '',
            shipping_province_code: '',
            shipping_ward: '',
            shipping_ward_code: '',
            product_notes: '',
            internal_order_code: '',
            shipping_carrier_id: DEFAULT_SHIPPING_CARRIER_ID,
            // "Thu cước" unset by default — neither "gửi" nor "nhận trả cước"
            // is chosen until the staff ticks one.
            shipping_fee_payer: undefined,
            // Most orders collect COD; can be unchecked for pre-paid/credit orders.
            collect_cod: true,
            items: [{ ...EMPTY_ITEM }],
        },
    })

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'items',
    })

    // Bộ đếm dòng dùng CHUNG cho sản phẩm và vật tư: dòng nào thêm sau thì số
    // lớn hơn nên luôn nằm dưới cùng bảng hàng hóa, kể cả khi xen kẽ hai loại
    // (FD #358). Dòng sản phẩm mặc định của form đã mang row_seq = 0.
    const rowSeqRef = useRef(1)
    const nextRowSeq = () => rowSeqRef.current++

    const handleCustomerChange = (customer: Customer | null) => {
        setSelectedCustomer(customer)
        form.setValue('customer_phone', customer?.phone ?? '', {
            shouldValidate: false,
        })
        if (customer?.address) {
            form.setValue('shipping_address', customer.address)
        }
        // Prefill the structured admin units from the customer's saved address.
        form.setValue('shipping_province', customer?.province ?? '')
        form.setValue('shipping_province_code', customer?.province_code ?? '')
        form.setValue('shipping_ward', customer?.ward ?? '')
        form.setValue('shipping_ward_code', customer?.ward_code ?? '')
        // Auto-apply group discount to all items. Dùng setValue theo path
        // thay vì useFieldArray.update() để giữ field.id ổn định — tránh
        // remount row, mất focus input đang gõ.
        const groupDiscount = customer?.customer_group?.discount_percent ?? 0
        const currentItems = form.getValues('items')
        currentItems.forEach((_, idx) => {
            form.setValue(`items.${idx}.discount_percent`, groupDiscount, {
                shouldDirty: true,
            })
        })
        // Khách đang dư → mặc định trừ vào số dư + tắt thu COD.
        const hasCredit = (customer?.current_debt ?? 0) < 0
        setApplyCredit(hasCredit)
        if (hasCredit) {
            form.setValue('collect_cod', false)
        }
    }

    // Cập nhật CK% của 1 dòng vật tư render inline
    // dưới dòng đơn. Match theo (line_key, material_id) — line_key = dòng đơn,
    // cho phép trùng sản phẩm/vật tư giữa các dòng mà CK vẫn độc lập.
    const handleMaterialDiscountChange = (
        materialId: string,
        lineKey: string,
        pctStr: string,
    ) => {
        const pct = Math.min(100, Math.max(0, Number(pctStr) || 0))
        setPackagingItems((prev) =>
            prev.map((p) =>
                p.packaging_material_id === materialId && p.line_key === lineKey
                    ? { ...p, discount_percent: pct }
                    : p,
            ),
        )
    }

    // SL + đơn giá vật tư CÓ GIÁ render inline: cho
    // sửa tay. Match theo (line_key, material_id).
    const handleMaterialQtyChange = (
        materialId: string,
        lineKey: string,
        qtyStr: string,
    ) => {
        const qty = Math.max(0, Number(qtyStr) || 0)
        setPackagingItems((prev) =>
            prev.map((p) =>
                p.packaging_material_id === materialId && p.line_key === lineKey
                    ? { ...p, quantity: qty }
                    : p,
            ),
        )
    }

    const handleMaterialPriceChange = (
        materialId: string,
        lineKey: string,
        priceStr: string,
    ) => {
        const price = Math.max(0, Number(priceStr) || 0)
        setPackagingItems((prev) =>
            prev.map((p) =>
                p.packaging_material_id === materialId && p.line_key === lineKey
                    ? { ...p, unit_price: price }
                    : p,
            ),
        )
    }

    const handleMaterialRemove = (materialId: string, lineKey: string) => {
        setPackagingItems((prev) =>
            prev.filter(
                (p) =>
                    !(
                        p.packaging_material_id === materialId &&
                        p.line_key === lineKey
                    ),
            ),
        )
    }

    // Vật tư THÊM TAY render ngang hàng sản phẩm. Khớp theo KHOÁ DÒNG chứ không
    // phải material_id: cùng một vật tư được thêm nhiều dòng.
    const patchMaterialRow = (
        rowKey: string,
        patch: (p: PackagingDraftItem) => PackagingDraftItem,
    ) =>
        setPackagingItems((prev) =>
            prev.map((p) => (rowKeyOf(p) === rowKey ? patch(p) : p)),
        )

    const handleMaterialRowQty = (rowKey: string, v: string) =>
        patchMaterialRow(rowKey, (p) => ({
            ...p,
            quantity: Math.max(0, Number(v) || 0),
        }))

    const handleMaterialRowPrice = (rowKey: string, v: string) =>
        patchMaterialRow(rowKey, (p) => ({
            ...p,
            unit_price: Math.max(0, Number(v) || 0),
        }))

    const handleMaterialRowDiscount = (rowKey: string, v: string) =>
        patchMaterialRow(rowKey, (p) => ({
            ...p,
            discount_percent: Math.min(100, Math.max(0, Number(v) || 0)),
        }))

    const handleMaterialRowRemove = (rowKey: string) =>
        setPackagingItems((prev) => prev.filter((p) => rowKeyOf(p) !== rowKey))

    const handleAddMaterialRow = (mat: PickedPackagingMaterial) => {
        setPackagingItems((prev) => [
            ...prev,
            {
                ...buildManualRow(
                    prev,
                    mat,
                    availableByMaterial,
                    newMaterialRowId(),
                ),
                row_seq: nextRowSeq(),
            },
        ])
    }

    // Xoá dòng SP: đồng thời bỏ vật tư inline gắn dòng đó (line_key = field.id)
    // để không còn vật tư "mồ côi" tính tiền/gửi payload trước khi editor refetch.
    const handleRemoveItem = (index: number) => {
        const removedFieldId = fields[index]?.id
        remove(index)
        if (removedFieldId) {
            setPackagingItems((prev) =>
                prev.filter((p) => p.line_key !== removedFieldId),
            )
        }
    }

    // Phase 5.2 — OrderItemRow đã chuyển sang Controller pattern; mỗi input
    // tự đăng ký vào FormProvider. Parent không cần bridge onChange nữa.

    const watchedItems = form.watch('items')
    const selectedLineCount =
        watchedItems.filter((item) => Boolean(item.product_id)).length +
        packagingItems.filter((item) => Boolean(item.packaging_material_id))
            .length
    useEffect(() => {
        workflow?.setContext({ lineCount: selectedLineCount })
    }, [workflow, selectedLineCount])

    // Vật tư của đơn giờ CHỈ do nhân viên tự thêm (#M-16) — đơn không tự sinh
    // theo định mức nữa, việc đó chuyển sang phiếu nhập kho hàng bán. Hook chỉ
    // còn bù số tồn cho các dòng thêm tay.
    const { availableByMaterial } = useOrderPackagingSync({
        value: packagingItems,
        onChange: setPackagingItems,
    })

    // Vật tư thêm tay = dòng không gắn dòng đơn nào. Vật tư theo định mức có
    // line_key và render dưới đúng sản phẩm của chúng (OrderMaterialSubRows).
    const manualMaterialRows = packagingItems.filter((p) => !p.line_key)

    // Thứ tự hiển thị của bảng hàng hóa: sản phẩm và vật tư thêm tay trộn chung
    // theo row_seq nên hai loại dòng xen kẽ được (FD #358). Vật tư theo định mức
    // không nằm ở đây — chúng luôn render ngay dưới dòng sản phẩm của mình.
    const displayRows = mergeOrderLines(
        fields.map((f) => f.row_seq),
        manualMaterialRows.map((mat) => mat.row_seq),
    )

    const itemSubtotal = watchedItems.reduce(
        (sum, item) =>
            sum +
            (item.quantity ?? 0) *
                (item.unit_price ?? 0) *
                (1 - (item.discount_percent ?? 0) / 100),
        0,
    )
    // line_key (field.id) → trạng thái tách/gộp của dòng SP. Dòng GỘP → giá vật
    // tư hiệu lực = 0 (giá nằm trong giá SP). Rebuild mỗi render theo watchedItems.
    const lineStateByKey = new Map<string, LineSplitState>(
        fields.map((f, idx) => [
            f.id,
            {
                priceIncludesMaterial:
                    watchedItems[idx]?.price_includes_material ?? false,
                materialPriceSplit:
                    watchedItems[idx]?.material_price_split ?? false,
            },
        ]),
    )
    const effectivePriceOf = (p: PackagingDraftItem) =>
        effectiveMaterialUnitPrice(
            p.unit_price ?? 0,
            lineStateByKey.get(p.line_key) ?? {
                priceIncludesMaterial: false,
                materialPriceSplit: false,
            },
        )

    // Tiền vật tư (giá vật tư) — chịu chiết khấu dòng như dòng hàng, cộng vào
    // base cùng SP trước VAT. Dòng GỘP dùng giá hiệu lực 0 (đã gồm trong giá SP).
    const materialTotal = packagingItems.reduce(
        (sum, p) =>
            sum +
            effectivePriceOf(p) *
                (p.quantity ?? 0) *
                (1 - (p.discount_percent ?? 0) / 100),
        0,
    )
    const baseAmount = itemSubtotal + materialTotal
    const roundedBase = Math.round(baseAmount)
    // M-78: VAT mặc định = tổng theo giá gồm VAT từng đơn vị − tiền hàng, để
    // tổng tròn theo giá niêm yết (giá chưa VAT thường lẻ: 100.000 / 1,08).
    const moneyLines: MoneyLine[] = [
        ...watchedItems.map((item) => ({
            quantity: item.quantity ?? 0,
            unitPrice: item.unit_price ?? 0,
            discountPercent: item.discount_percent ?? 0,
        })),
        ...packagingItems.map((p) => ({
            quantity: p.quantity ?? 0,
            unitPrice: effectivePriceOf(p),
            discountPercent: p.discount_percent ?? 0,
        })),
    ]
    // VAT làm tròn về số nguyên (VND không có subunit). Người bán gõ tay số tiền
    // (override) thì dùng đúng số đó; ngược lại suy từ %.
    const vatAmount =
        vatAmountOverride != null
            ? vatAmountOverride
            : vatPercent > 0
              ? vatFromGross(moneyLines, 0, vatPercent, roundedBase)
              : 0
    const totalAmount = roundedBase + vatAmount

    // Base đổi (thêm/bớt hàng, đổi giá) → bỏ override để tiền VAT bám lại theo %.
    useEffect(() => {
        setVatAmountOverride(null)
    }, [roundedBase])

    // Sửa % → tính lại tiền (bỏ override). Sửa tiền → back-calc % + giữ đúng tiền.
    const handleVatPercentChange = (v: number) => {
        vatTouched.current = true
        setVatAmountOverride(null)
        setVatPercent(Number.isFinite(v) && v >= 0 ? v : 0)
    }
    const handleVatAmountChange = (amt: number) => {
        vatTouched.current = true
        const safe = Number.isFinite(amt) && amt >= 0 ? Math.round(amt) : 0
        setVatAmountOverride(safe)
        // % chỉ để hiển thị/lưu nhãn → làm tròn 2 số (khớp cột DECIMAL(5,2)).
        setVatPercent(
            roundedBase > 0
                ? Math.round((safe / roundedBase) * 100 * 100) / 100
                : 0,
        )
    }

    // Giữ payload cuối cùng để retry với allow_awaiting=true khi cashier confirm
    // popup AwaitingPackaging (tránh phải re-build và re-validate form).
    const [pendingPayload, setPendingPayload] =
        useState<CreateOrderRequest | null>(null)

    // Đơn vị (ĐVT) per product_id để dialog hiển thị cột "ĐVT" — lấy từ form
    // items (đã bind unit khi chọn sản phẩm).
    const unitByProductId = form
        .watch('items')
        .reduce<Record<string, string | undefined>>((acc, item) => {
            if (item.product_id && !acc[item.product_id]) {
                acc[item.product_id] = item.unit
            }
            return acc
        }, {})

    const submitPayload = async (
        payload: CreateOrderRequest,
        itemCount: number,
    ) => {
        try {
            workflow?.setContext({ lineCount: selectedLineCount })
            const endWait = workflow?.submit()
            let response
            try {
                response = await createMutation.mutateAsync(payload)
            } catch (error) {
                workflow?.step('api_failed')
                throw error
            } finally {
                endWait?.()
            }
            if (response.data?.id) {
                workflow?.created(response.data.id)
                workflow?.finish('api_confirmed', response.data.id)
            }
            // Reference point for the analytics drift check, not a sales
            // count — see the policy note in @/lib/posthog.
            try {
                posthog.capture(
                    'sales_order_created',
                    orderCreatedProps({
                        orderId: response.data?.id,
                        sourceChannel: payload.source_channel,
                        itemCount,
                        totalAmount,
                        customerId: payload.customer_id,
                        walkInCustomerId: WALKIN_CUSTOMER_ID,
                        extra: {
                            allow_awaiting: payload.allow_awaiting ?? false,
                            ...(workflow
                                ? { attempt_id: workflow.attemptId }
                                : {}),
                        },
                    }),
                )
            } catch {
                /* Analytics must not interrupt saving. */
            }
            const appliedCredit = response.data?.applied_credit ?? 0
            if (appliedCredit > 0) {
                toast.success(
                    `${m.order_credit_applied()}: ${formatVnd(appliedCredit)}`,
                )
            }
            setPendingPayload(null)
            setAwaitShortfalls(null)
            onSuccess?.()
        } catch {
            // errors handled via toast hoặc popup AwaitingPackaging
        }
    }

    const handleSubmit = form.handleSubmit(
        async (data) => {
            // Đơn phải có "dòng bán được": ≥1 sản phẩm HOẶC ≥1 vật tư có đơn giá
            // (đơn chỉ vật tư). Vật tư ở state riêng nên kiểm ở đây, ngoài zod.
            if (!orderHasSellableLine(data.items, packagingItems)) {
                workflow?.step('validation_failed', {
                    fieldGroup: 'items',
                    validationCode: 'required',
                })
                toast.error(m.validation_order_needs_sellable_line())
                return
            }
            // Walk-in fallback when no customer is picked.
            const buyer = selectedCustomer ?? WALKIN_CUSTOMER
            // field.id (line_key) → index dòng đơn; data.items cùng thứ tự với fields.
            const lineIndexByKey = new Map(fields.map((f, i) => [f.id, i]))
            // Thứ tự dòng người bán đã nhập (sản phẩm xen kẽ vật tư) được đánh lại
            // thành sort_order 0..n-1 để BE lưu và mọi màn đọc lại đúng thứ tự.
            const payloadMaterials = packagingItems.filter(
                (p) => p.quantity > 0,
            )
            const { itemSortOrders, materialSortOrders } = buildLineSortOrders({
                items: fields.map((f) => ({ key: f.id, row_seq: f.row_seq })),
                materials: payloadMaterials.map((p) => ({
                    line_key: p.line_key,
                    row_seq: p.row_seq,
                })),
            })
            const payload: CreateOrderRequest = {
                source_channel: data.source_channel,
                customer_id: buyer.id,
                customer_name: buyer.name,
                customer_phone: data.customer_phone.trim(),
                shipping_address: data.shipping_address,
                shipping_province: data.shipping_province || undefined,
                shipping_province_code:
                    data.shipping_province_code || undefined,
                shipping_ward: data.shipping_ward || undefined,
                shipping_ward_code: data.shipping_ward_code || undefined,
                shipping_carrier_id:
                    data.shipping_carrier_id || DEFAULT_SHIPPING_CARRIER_ID,
                // Freight amount is no longer entered here — the depot settles it
                // later. Only the "Thu cước" intent (payer) is recorded.
                shipping_fee: undefined,
                shipping_fee_payer: data.shipping_fee_payer,
                // defaultValues guarantees this is always set; ?? true is a type-safe fallback.
                collect_cod: data.collect_cod ?? null,
                // Cổng "Xuất hóa đơn điện tử": tick → gửi MISA khi giao thành công.
                issue_einvoice: einvoiceEnabled ? invoiceBuyer.enabled : false,
                // Người mua công ty (HĐĐT): chỉ gửi khi bật e-invoice + tick.
                invoice_buyer: einvoiceEnabled
                    ? buildInvoiceBuyerPayload(invoiceBuyer, 'create')
                    : undefined,
                apply_credit: applyCredit,
                product_notes: data.product_notes || undefined,
                internal_order_code: data.internal_order_code || undefined,
                // VAT người bán chốt trên form: gửi cả % và tiền để BE snapshot đúng.
                vat_percent: vatPercent,
                vat_amount: vatAmount,
                items: data.items.map((item, idx) => ({
                    product_id: item.product_id,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    discount_percent: item.discount_percent || undefined,
                    material_price_split:
                        item.material_price_split || undefined,
                    sort_order: itemSortOrders[idx],
                })),
                packaging_items: payloadMaterials.map((p, idx) => ({
                    packaging_material_id: p.packaging_material_id,
                    quantity: p.quantity,
                    source: p.source,
                    // Dòng GỘP gửi giá 0 (giá vật tư nằm trong giá SP); dòng TÁCH
                    // gửi giá cấu hình. Vẫn trừ kho/PXK bình thường (chỉ giá 0).
                    unit_price: effectivePriceOf(p),
                    discount_percent: p.discount_percent,
                    // Rỗng cho vật tư thêm tay → OMIT (BE bind uuid.UUID, chuỗi ''
                    // gây "Invalid request body"; field trống = zero-uuid hợp lệ).
                    product_id: p.product_id || undefined,
                    // Mọi vật tư gắn dòng đơn (line_key = field.id, định mức hay
                    // không) → index dòng để BE lưu order_item_id.
                    order_item_index: p.line_key
                        ? lineIndexByKey.get(p.line_key)
                        : undefined,
                    sort_order: materialSortOrders[idx],
                })),
            }
            setPendingPayload(payload)
            await submitPayload(payload, data.items.length)
        },
        (errors) => trackOrderValidation(errors, workflow),
    )

    const handleAwaitConfirm = async () => {
        if (!pendingPayload) return
        const retryPayload: CreateOrderRequest = {
            ...pendingPayload,
            allow_awaiting: true,
        }
        await submitPayload(retryPayload, retryPayload.items?.length ?? 0)
    }

    return (
        <FormProvider {...form}>
            <form
                noValidate
                onSubmit={handleSubmit}
                onFocusCapture={(e) => trackOrderField(e.target, workflow)}
                onClickCapture={(e) => trackOrderField(e.target, workflow)}
                onInputCapture={(e) => trackOrderField(e.target, workflow)}
            >
                {/* 2 cột ở lg+: trái = thông tin đơn (cuộn theo trang), phải =
                    Sản phẩm + Vật tư + tổng tiền trong 1 rail dính top, cuộn
                    nội bộ → khối Sản phẩm luôn hiển thị khi điền thông tin. */}
                <div className="grid gap-5 lg:grid-cols-[minmax(360px,420px)_minmax(0,1fr)] lg:items-start">
                    <div className="space-y-5">
                        <FormSection
                            icon={<Users />}
                            data-order-group="customer"
                            title={m.order_customer()}
                        >
                            {/* Customer search — outside schema, managed via useState */}
                            <div className="space-y-1">
                                <label className="text-sm font-medium">
                                    {m.order_customer()}
                                </label>
                                <CustomerSearchSelect
                                    value={selectedCustomer}
                                    onChange={handleCustomerChange}
                                    defaultCustomerId={defaultCustomerId}
                                    workflow={workflow}
                                />
                                <p className="text-xs text-muted-foreground">
                                    {m.order_customer_walkin_hint()}
                                </p>
                                {selectedCustomer &&
                                    (selectedCustomer.current_debt ?? 0) !==
                                        0 &&
                                    ((selectedCustomer.current_debt ?? 0) >
                                    0 ? (
                                        <div className="inline-flex w-fit items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1 text-sm font-semibold text-red-700">
                                            {m.order_customer_current_debt()}:{' '}
                                            {formatVnd(
                                                selectedCustomer.current_debt ??
                                                    0,
                                            )}
                                        </div>
                                    ) : (
                                        <div className="inline-flex w-fit items-center gap-1.5 rounded-md bg-green-50 px-2.5 py-1 text-sm font-semibold text-green-700">
                                            {m.order_customer_prepaid()}:{' '}
                                            {formatVnd(
                                                -(
                                                    selectedCustomer.current_debt ??
                                                    0
                                                ),
                                            )}
                                        </div>
                                    ))}
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <Field
                                    name="source_channel"
                                    label={m.customer_sales_channel()}
                                    required
                                >
                                    <Controller
                                        name="source_channel"
                                        control={form.control}
                                        render={({ field }) => (
                                            <Select
                                                value={field.value}
                                                onValueChange={field.onChange}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {getSourceChannels().map(
                                                        (c) => (
                                                            <SelectItem
                                                                key={c.value}
                                                                value={c.value}
                                                            >
                                                                {c.label}
                                                            </SelectItem>
                                                        ),
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </Field>

                                <Field
                                    name="customer_phone"
                                    label={m.customer_phone_label()}
                                    required
                                >
                                    <Input
                                        id="customer_phone"
                                        type="tel"
                                        inputMode="tel"
                                        autoComplete="tel"
                                        {...form.register('customer_phone')}
                                    />
                                </Field>
                            </div>
                        </FormSection>

                        <FormSection
                            icon={<Truck />}
                            data-order-group="shipping"
                            title={m.order_section_delivery()}
                        >
                            <Field
                                name="shipping_address"
                                label={m.customer_shipping_address()}
                                required
                            >
                                <Input
                                    id="shipping_address"
                                    {...form.register('shipping_address')}
                                />
                            </Field>

                            <div>
                                <ProvinceWardPicker
                                    value={{
                                        provinceCode:
                                            form.watch(
                                                'shipping_province_code',
                                            ) || undefined,
                                        provinceName:
                                            form.watch('shipping_province'),
                                        wardCode:
                                            form.watch('shipping_ward_code') ||
                                            undefined,
                                        wardName: form.watch('shipping_ward'),
                                    }}
                                    onChange={(next) => {
                                        form.setValue(
                                            'shipping_province_code',
                                            next.provinceCode ?? '',
                                            { shouldValidate: true },
                                        )
                                        form.setValue(
                                            'shipping_province',
                                            next.provinceName ?? '',
                                        )
                                        form.setValue(
                                            'shipping_ward_code',
                                            next.wardCode ?? '',
                                            { shouldValidate: true },
                                        )
                                        form.setValue(
                                            'shipping_ward',
                                            next.wardName ?? '',
                                        )
                                    }}
                                />
                                {(form.formState.errors
                                    .shipping_province_code ||
                                    form.formState.errors
                                        .shipping_ward_code) && (
                                    <p className="mt-1 text-sm text-destructive">
                                        {form.formState.errors
                                            .shipping_province_code?.message ??
                                            form.formState.errors
                                                .shipping_ward_code?.message}
                                    </p>
                                )}
                            </div>

                            <Field
                                name="shipping_carrier_id"
                                label={m.order_form_carrier_label()}
                            >
                                <Controller
                                    control={form.control}
                                    name="shipping_carrier_id"
                                    render={({ field }) => (
                                        <ShippingCarrierSelect
                                            id="shipping_carrier_id"
                                            value={field.value}
                                            onValueChange={field.onChange}
                                        />
                                    )}
                                />
                            </Field>

                            {/* Trừ vào số dư khách — chỉ hiện khi khách đang dư.
                                Bật → tự tắt + khoá thu COD. */}
                            {customerCredit > 0 && (
                                <label className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 p-3 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={applyCredit}
                                        onChange={(e) => {
                                            const v = e.target.checked
                                            setApplyCredit(v)
                                            // Bật → tắt thu COD (cấn số dư, không
                                            // thu tiền). Tắt → khôi phục thu COD
                                            // = "Có" (mặc định), tránh kẹt ở
                                            // "Không" sau khi bỏ tick.
                                            form.setValue('collect_cod', !v, {
                                                shouldValidate: true,
                                            })
                                        }}
                                        className="size-4 accent-primary"
                                    />
                                    <span className="font-medium text-green-800">
                                        {m.order_credit_apply_label()} (
                                        {formatVnd(customerCredit)})
                                    </span>
                                </label>
                            )}

                            {/* Thu tiền (COD) + Thu cước vận chuyển — dùng chung
                                component với mobile / màn sửa đơn. */}
                            <div
                                className="contents"
                                data-order-group="payment"
                            >
                                <CodFreightFields
                                    idPrefix="order-desktop"
                                    codDisabled={applyCredit}
                                    collectCod={form.watch('collect_cod')}
                                    onCollectCodChange={(v) =>
                                        form.setValue('collect_cod', v, {
                                            shouldValidate: true,
                                        })
                                    }
                                    feePayer={form.watch('shipping_fee_payer')}
                                    onFeePayerChange={(p) =>
                                        form.setValue('shipping_fee_payer', p, {
                                            shouldValidate: true,
                                        })
                                    }
                                />
                            </div>
                            {/* Ghi chú — gộp vào cuối phần giao hàng. */}
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                                <Field
                                    name="internal_order_code"
                                    label={m.order_internal_code_label()}
                                >
                                    <Input
                                        id="internal_order_code"
                                        maxLength={64}
                                        placeholder={m.order_internal_code_placeholder()}
                                        {...form.register(
                                            'internal_order_code',
                                        )}
                                    />
                                </Field>
                                <Field
                                    name="product_notes"
                                    label={m.customer_product_notes()}
                                >
                                    <textarea
                                        id="product_notes"
                                        rows={2}
                                        className="border-input focus-visible:border-ring w-full resize-y rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs outline-none"
                                        {...form.register('product_notes')}
                                    />
                                </Field>
                            </div>
                        </FormSection>
                    </div>

                    <div className="space-y-5">
                        <FormSection
                            icon={<ShoppingCart />}
                            data-order-group="items"
                            title={m.order_products()}
                            action={
                                <div className="flex items-center gap-2">
                                    {/* Vật tư đã chọn vẫn chọn tiếp được. */}
                                    <div className="w-56">
                                        <PackagingMaterialPicker
                                            onPick={handleAddMaterialRow}
                                            placeholder={m.packaging_add_material()}
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() =>
                                            append({
                                                ...EMPTY_ITEM,
                                                discount_percent:
                                                    selectedCustomer
                                                        ?.customer_group
                                                        ?.discount_percent ?? 0,
                                                row_seq: nextRowSeq(),
                                            })
                                        }
                                    >
                                        <Plus className="size-4" />
                                        {m.common_add_row()}
                                    </Button>
                                </div>
                            }
                        >
                            <div className="overflow-x-auto rounded-lg border">
                                <table className="w-full table-fixed text-sm">
                                    <colgroup>
                                        <col className="w-[30%]" />
                                        <col className="w-[9%]" />
                                        <col className="w-[9%]" />
                                        <col className="w-[18%]" />
                                        <col className="w-[9%]" />
                                        <col className="w-[17%]" />
                                        <col className="w-[8%]" />
                                    </colgroup>
                                    <thead className="bg-primary/5 text-[11px] uppercase tracking-wider text-primary/80">
                                        <tr>
                                            <th className="px-3 py-2.5 text-left font-semibold">
                                                {m.order_products()}
                                            </th>
                                            <th className="px-3 py-2.5 text-left font-semibold">
                                                {m.order_unit_col()}
                                            </th>
                                            <th className="px-3 py-2.5 text-left font-semibold">
                                                {m.order_quantity()}
                                            </th>
                                            <th className="px-3 py-2.5 text-left font-semibold">
                                                {m.order_unit_price()}
                                            </th>
                                            <th className="px-3 py-2.5 text-left font-semibold">
                                                {m.order_discount_short()}
                                                {selectedCustomer?.customer_group &&
                                                    selectedCustomer
                                                        .customer_group
                                                        .discount_percent >
                                                        0 && (
                                                        <span className="ml-1 text-[10px] font-normal normal-case text-muted-foreground">
                                                            (
                                                            {
                                                                selectedCustomer
                                                                    .customer_group
                                                                    .name
                                                            }
                                                            :{' '}
                                                            {
                                                                selectedCustomer
                                                                    .customer_group
                                                                    .discount_percent
                                                            }
                                                            %)
                                                        </span>
                                                    )}
                                            </th>
                                            <th className="px-3 py-2.5 text-right font-semibold">
                                                {m.order_subtotal()}
                                            </th>
                                            <th className="px-3 py-2.5" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayRows.map((row) => {
                                            if (row.kind === 'material') {
                                                const mat =
                                                    manualMaterialRows[
                                                        row.index
                                                    ]
                                                return (
                                                    <OrderMaterialRow
                                                        key={rowKeyOf(mat)}
                                                        item={mat}
                                                        onQtyChange={
                                                            handleMaterialRowQty
                                                        }
                                                        onPriceChange={
                                                            handleMaterialRowPrice
                                                        }
                                                        onDiscountChange={
                                                            handleMaterialRowDiscount
                                                        }
                                                        onRemove={
                                                            handleMaterialRowRemove
                                                        }
                                                    />
                                                )
                                            }
                                            const field = fields[row.index]
                                            const materialRows =
                                                packagingItems.filter(
                                                    (p) =>
                                                        p.line_key === field.id,
                                                )
                                            return (
                                                <Fragment key={field.id}>
                                                    <OrderItemRowRhf
                                                        workflow={workflow}
                                                        index={row.index}
                                                        warehouseId={
                                                            defaultWarehouseId
                                                        }
                                                        onRemove={
                                                            handleRemoveItem
                                                        }
                                                    />
                                                    <OrderMaterialSubRows
                                                        materials={materialRows}
                                                        effectivePriceOf={
                                                            effectivePriceOf
                                                        }
                                                        onQtyChange={
                                                            handleMaterialQtyChange
                                                        }
                                                        onPriceChange={
                                                            handleMaterialPriceChange
                                                        }
                                                        onDiscountChange={
                                                            handleMaterialDiscountChange
                                                        }
                                                        onRemove={
                                                            handleMaterialRemove
                                                        }
                                                    />
                                                </Fragment>
                                            )
                                        })}
                                    </tbody>
                                    <tfoot
                                        data-order-group="payment"
                                        className="border-t bg-muted/30 tabular-nums"
                                    >
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="px-3 py-2 text-right text-muted-foreground"
                                            >
                                                {m.order_items_subtotal()}:
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                {formatVND(itemSubtotal)}
                                            </td>
                                            <td />
                                        </tr>
                                        {materialTotal > 0 && (
                                            <tr>
                                                <td
                                                    colSpan={5}
                                                    className="px-3 py-2 text-right text-muted-foreground"
                                                >
                                                    {m.order_material_total()}:
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    {formatVND(materialTotal)}
                                                </td>
                                                <td />
                                            </tr>
                                        )}
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="px-3 py-2 text-right text-muted-foreground"
                                            >
                                                <span className="inline-flex items-center justify-end gap-1.5">
                                                    {m.order_vat_field_label()}
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        step="0.1"
                                                        inputMode="decimal"
                                                        aria-label={m.order_vat_field_label()}
                                                        value={vatPercent}
                                                        onChange={(e) =>
                                                            handleVatPercentChange(
                                                                e.target
                                                                    .valueAsNumber,
                                                            )
                                                        }
                                                        className="focus-visible:border-ring w-16 rounded-md border bg-transparent px-2 py-1 text-right text-sm outline-none"
                                                    />
                                                    %:
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step="1"
                                                    inputMode="numeric"
                                                    aria-label={m.order_vat_amount_label()}
                                                    value={Math.round(
                                                        vatAmount,
                                                    )}
                                                    onChange={(e) =>
                                                        handleVatAmountChange(
                                                            e.target
                                                                .valueAsNumber,
                                                        )
                                                    }
                                                    className="focus-visible:border-ring w-28 rounded-md border bg-transparent px-2 py-1 text-right text-sm outline-none"
                                                />
                                            </td>
                                            <td />
                                        </tr>
                                        {/* Tổng cộng — desktop hiển thị ngay dưới
                                            VAT trong bảng; mobile/tablet vẫn dùng
                                            thanh dính đáy bên dưới. */}
                                        <tr className="hidden border-t lg:table-row">
                                            <td
                                                colSpan={5}
                                                className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                                            >
                                                {m.order_grand_total()}
                                            </td>
                                            <td className="px-3 py-2.5 text-right text-base font-bold text-primary">
                                                {formatVND(totalAmount)}
                                            </td>
                                            <td />
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </FormSection>

                        {/* Người mua công ty (HĐĐT) — dưới phần Sản phẩm để cân 2
                            cột; chỉ hiện khi bật e-invoice. */}
                        {einvoiceEnabled && (
                            <InvoiceBuyerFields
                                idPrefix="order-desktop"
                                value={invoiceBuyer}
                                onChange={(patch) =>
                                    setInvoiceBuyer((prev) =>
                                        applyBuyerPatch(
                                            prev,
                                            patch,
                                            selectedCustomer,
                                        ),
                                    )
                                }
                            />
                        )}

                        {/* Thanh hành động. Mobile/tablet: dính đáy, hiện tổng +
                    nút để người bán luôn thấy mà không phải cuộn. Desktop (lg):
                    bỏ chrome card + không dính, ẩn tổng (đã nằm ở tfoot bảng SP),
                    chỉ còn hàng nút canh phải ngay dưới phần tổng cộng. */}
                        <div className="sticky bottom-0 z-10 flex flex-col gap-3 rounded-xl border bg-card/95 px-5 py-3.5 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80 sm:flex-row sm:items-center sm:justify-between lg:static lg:z-auto lg:justify-end lg:rounded-none lg:border-0 lg:bg-transparent lg:px-0 lg:py-0 lg:shadow-none lg:backdrop-blur-none lg:supports-[backdrop-filter]:bg-transparent">
                            <div className="lg:hidden">
                                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                                    {m.order_grand_total()}
                                </div>
                                <div className="text-2xl font-bold tabular-nums text-primary">
                                    {formatVND(totalAmount)}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {onCancel && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={onCancel}
                                    >
                                        {m.common_cancel()}
                                    </Button>
                                )}
                                <Button
                                    type="submit"
                                    size="lg"
                                    disabled={createMutation.isPending}
                                >
                                    {createMutation.isPending
                                        ? m.common_creating()
                                        : m.dashboard_create_order()}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            <AwaitingPackagingDialog
                open={awaitShortfalls !== null}
                onOpenChange={(open) => {
                    if (!open) setAwaitShortfalls(null)
                }}
                shortfalls={awaitShortfalls ?? []}
                unitByProductId={unitByProductId}
                onConfirm={handleAwaitConfirm}
                submitting={createMutation.isPending}
            />
        </FormProvider>
    )
}
